import { Anchor, Text } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { api } from '../../lib/api';
import type { TodoDueInput } from '../../lib/api';
import { TODO_QUERY_KEYS, formatDayLabel } from './todoLabels';

/**
 * Die Aktionen, die Liste, Detail und Ticket-Cockpit gleichermaßen anbieten. Jede lädt danach
 * alle Aufgaben-Abfragen neu (Listen, Zähler, Ticket-Panel, Übersicht) — sie hängen alle an
 * `['todos', …]`.
 */
export function useTodoActions() {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: TODO_QUERY_KEYS.all });
    void queryClient.invalidateQueries({ queryKey: TODO_QUERY_KEYS.ticketCockpits });
    void queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    void queryClient.invalidateQueries({ queryKey: ['notifications-list'] });
  };
  const onError = (e: Error) => notifications.show({ title: 'Fehler', message: e.message, color: 'red' });

  const reopen = useMutation({
    mutationFn: (id: string) => api.reopenTodo(id),
    onSuccess: refresh,
    onError,
  });

  /** Persönliches sofort abhaken — mit „Rückgängig", falls es der falsche Haken war. */
  const completeNow = useMutation({
    mutationFn: (id: string) => api.completeTodo(id, {}),
    onSuccess: (_res, id) => {
      refresh();
      const nid = `todo-done-${id}`;
      notifications.show({
        id: nid,
        title: 'Erledigt',
        color: 'green',
        autoClose: 6000,
        message: (
          <Text size="sm">
            <Anchor
              component="button"
              size="sm"
              onClick={() => {
                notifications.hide(nid);
                reopen.mutate(id);
              }}
            >
              Rückgängig
            </Anchor>
          </Text>
        ),
      });
    },
    onError,
  });

  const snooze = useMutation({
    mutationFn: ({ id, due }: { id: string; due: TodoDueInput }) => api.snoozeTodo(id, due),
    onSuccess: (_res, { due }) => {
      refresh();
      notifications.show({
        title: 'Verschoben',
        message: `${formatDayLabel(due.date)}${due.time ? `, ${due.time}` : ''}`,
        color: 'blue',
      });
    },
    onError,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteTodo(id),
    onSuccess: refresh,
    onError,
  });

  const extendEscalation = useMutation({
    mutationFn: (id: string) => api.extendTodoEscalation(id),
    onSuccess: refresh,
    onError,
  });

  return { refresh, reopen, completeNow, snooze, remove, extendEscalation };
}
